import React from 'react';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import gmailLogo from '../../../../assets/gmail-logo.svg';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_INPUT_CLASS,
} from '../../utils/settingsSectionStyles';
import type { InboxAccount } from './types';

type AddConnectKind = 'choose' | 'imap';
type ImapConnectStage = 'idle' | 'lookup-domain' | 'lookup-provider' | 'connect-mailbox';

function GmailChoiceIcon() {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-app-surface ring-1 ring-slate-200">
      <img src={gmailLogo} alt="" className="h-6 w-6" />
    </div>
  );
}

function ImapChoiceIcon() {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-pipedrive-blue ring-1 ring-blue-100">
      <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
        <rect x="4" y="5" width="16" height="5" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" />
        <rect x="4" y="14" width="16" height="5" rx="1.5" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M8 8h.01M8 17h.01" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        <path d="M12 8h4M12 17h4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function imapStageLabel(stage: ImapConnectStage): string {
  switch (stage) {
    case 'lookup-domain':
      return 'E-Mail-Domain wird geprüft…';
    case 'lookup-provider':
      return 'Mail-Anbieter wird erkannt…';
    case 'connect-mailbox':
      return 'Postfach wird verbunden…';
    default:
      return '';
  }
}

const emptyImapForm = () => ({
  email: '',
  password: '',
  showAdvanced: false,
  imap_host: '',
  imap_port: '993',
  smtp_host: '',
  smtp_port: '587',
});

export function MailboxConnectForm({
  variant = 'card',
  onCancel,
  onImapConnected,
  onError,
  onBeforeGoogleRedirect,
}: {
  variant?: 'card' | 'embedded';
  onCancel: () => void;
  onImapConnected: (account: InboxAccount) => void;
  onError: (message: string | null) => void;
  onBeforeGoogleRedirect?: () => void;
}) {
  const [kind, setKind] = React.useState<AddConnectKind>('choose');
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [imapForm, setImapForm] = React.useState(emptyImapForm);
  const [imapConnectStage, setImapConnectStage] = React.useState<ImapConnectStage>('idle');
  const [adding, setAdding] = React.useState(false);
  const googleStartRef = React.useRef(false);

  React.useEffect(() => {
    const resetGoogleStart = () => {
      googleStartRef.current = false;
    };
    window.addEventListener('pageshow', resetGoogleStart);
    return () => window.removeEventListener('pageshow', resetGoogleStart);
  }, []);

  const goChoose = () => {
    if (adding) return;
    setKind('choose');
  };

  const startGoogleOAuth = () => {
    if (googleStartRef.current) return;
    googleStartRef.current = true;
    onError(null);
    onBeforeGoogleRedirect?.();
    fetch('/api/inbox/oauth/google/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_private: isPrivate }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d?.error || 'OAuth-Start fehlgeschlagen');
        if (!d?.authUrl) throw new Error('Keine OAuth-URL erhalten');
        window.location.href = d.authUrl;
      })
      .catch((err) => {
        googleStartRef.current = false;
        onError(err.message || 'OAuth-Start fehlgeschlagen');
      });
  };

  const connectImap = async () => {
    const email = imapForm.email.trim();
    if (!email) {
      onError('Bitte eine E-Mail-Adresse eingeben.');
      return;
    }
    if (!imapForm.password) {
      onError('Bitte ein Passwort eingeben.');
      return;
    }
    setAdding(true);
    onError(null);
    setImapConnectStage('lookup-domain');
    const stageTimers = [
      window.setTimeout(() => setImapConnectStage('lookup-provider'), 450),
      window.setTimeout(() => setImapConnectStage('connect-mailbox'), 1100),
    ];

    const body: Record<string, unknown> = {
      password: imapForm.password,
      email: email.toLowerCase(),
      is_private: isPrivate,
    };
    if (imapForm.showAdvanced) {
      if (imapForm.imap_host.trim()) body.imap_host = imapForm.imap_host.trim();
      if (imapForm.smtp_host.trim()) body.smtp_host = imapForm.smtp_host.trim();
      if (imapForm.imap_port.trim()) body.imap_port = Number(imapForm.imap_port);
      if (imapForm.smtp_port.trim()) body.smtp_port = Number(imapForm.smtp_port);
    }

    try {
      const res = await fetch('/api/inbox/imap/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error || 'Verbindung fehlgeschlagen');
      onImapConnected(data as InboxAccount);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Verbindung fehlgeschlagen');
    } finally {
      stageTimers.forEach((timer) => window.clearTimeout(timer));
      setImapConnectStage('idle');
      setAdding(false);
    }
  };

  const choiceButtons = (
    <div className="space-y-3 w-full max-w-full min-w-0">
      <div>
        <p className={SETTINGS_FIELD_LABEL}>Art des Postfachs</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setIsPrivate(false)}
            className={`inline-flex h-9 items-center justify-center rounded-md border px-3.5 text-sm font-medium transition-colors ${
              !isPrivate
                ? 'border-pipedrive-blue bg-blue-50 text-pipedrive-blue'
                : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
            }`}
          >
            Allgemein
          </button>
          <button
            type="button"
            onClick={() => setIsPrivate(true)}
            className={`inline-flex h-9 items-center justify-center rounded-md border px-3.5 text-sm font-medium transition-colors ${
              isPrivate
                ? 'border-pipedrive-blue bg-blue-50 text-pipedrive-blue'
                : 'border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
            }`}
          >
            Privat
          </button>
        </div>
        <p className="mt-2 text-xs text-app-text-subtle">
          {isPrivate
            ? 'Privat: nur Sie sehen dieses Postfach und seine E-Mails.'
            : 'Allgemein: das Postfach ist für alle Mitglieder der Instanz sichtbar.'}
        </p>
      </div>
      <p className={SETTINGS_FIELD_LABEL}>Postfachtyp auswählen</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={startGoogleOAuth}
          className="rounded-lg border border-app-border bg-app-surface p-4 text-left hover:border-pipedrive-blue hover:bg-blue-50/40 transition-colors"
        >
          <div className="flex items-start gap-3">
            <GmailChoiceIcon />
            <div>
              <p className="text-sm font-semibold text-app-text">Gmail</p>
              <p className="mt-1 text-xs text-app-text-muted">Anmeldung über Google — empfohlen für Gmail-Nutzer.</p>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setKind('imap')}
          className="rounded-lg border border-app-border bg-app-surface p-4 text-left hover:border-pipedrive-blue hover:bg-blue-50/40 transition-colors"
        >
          <div className="flex items-start gap-3">
            <ImapChoiceIcon />
            <div>
              <p className="text-sm font-semibold text-app-text">E-Mail (IMAP)</p>
              <p className="mt-1 text-xs text-app-text-muted">IONOS, Strato, eigener Server — E-Mail und Passwort.</p>
            </div>
          </div>
        </button>
      </div>
      <div className="flex justify-end pt-4">
        <button type="button" onClick={onCancel} className={SETTINGS_BTN_OUTLINE}>
          Abbrechen
        </button>
      </div>
    </div>
  );

  const body =
    kind === 'choose' ? (
      choiceButtons
    ) : (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void connectImap();
        }}
        className="space-y-4 w-full max-w-full min-w-0"
      >
        <div>
          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>E-Mail-Adresse</label>
          <input
            type="email"
            value={imapForm.email}
            onChange={(e) => setImapForm((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="name@firma.de"
            className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
            required
          />
        </div>
        <div>
          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Passwort</label>
          <input
            type="password"
            value={imapForm.password}
            onChange={(e) => setImapForm((prev) => ({ ...prev, password: e.target.value }))}
            className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
            required
            autoComplete="off"
          />
          <p className="mt-1 text-xs text-app-text-subtle">
            Bei Zwei-Faktor-Authentifizierung wird oft ein App-Passwort des Anbieters benötigt.
          </p>
        </div>
        {adding && imapConnectStage !== 'idle' ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
            <div className="flex items-center gap-2">
              <LoadingSpinner />
              <span className="font-medium">{imapStageLabel(imapConnectStage)}</span>
            </div>
            <p className="mt-1 text-xs text-blue-800">
              Wir erkennen den Anbieter automatisch und richten das Postfach danach ein.
            </p>
          </div>
        ) : null}
        <div>
          <button
            type="button"
            onClick={() => setImapForm((prev) => ({ ...prev, showAdvanced: !prev.showAdvanced }))}
            className="inline-flex items-center gap-1 text-sm font-medium text-pipedrive-blue hover:underline"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${imapForm.showAdvanced ? 'rotate-180' : ''}`} />
            Erweiterte Einstellungen
          </button>
        </div>
        {imapForm.showAdvanced ? (
          <div className="grid gap-3 sm:grid-cols-2 rounded-lg border border-app-border bg-app-surface-muted p-3">
            <div>
              <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>IMAP-Host</label>
              <input
                type="text"
                value={imapForm.imap_host}
                onChange={(e) => setImapForm((prev) => ({ ...prev, imap_host: e.target.value }))}
                placeholder="imap.beispiel.de"
                className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
              />
            </div>
            <div>
              <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>IMAP-Port</label>
              <input
                type="text"
                value={imapForm.imap_port}
                onChange={(e) => setImapForm((prev) => ({ ...prev, imap_port: e.target.value }))}
                placeholder="993"
                className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
              />
            </div>
            <div>
              <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>SMTP-Host</label>
              <input
                type="text"
                value={imapForm.smtp_host}
                onChange={(e) => setImapForm((prev) => ({ ...prev, smtp_host: e.target.value }))}
                placeholder="smtp.beispiel.de"
                className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
              />
            </div>
            <div>
              <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>SMTP-Port</label>
              <input
                type="text"
                value={imapForm.smtp_port}
                onChange={(e) => setImapForm((prev) => ({ ...prev, smtp_port: e.target.value }))}
                placeholder="587"
                className={`${SETTINGS_INPUT_CLASS} bg-app-surface`}
              />
            </div>
          </div>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-app-border pt-4">
          <button type="button" onClick={onCancel} disabled={adding} className={SETTINGS_BTN_OUTLINE}>
            Abbrechen
          </button>
          <button type="submit" disabled={adding} className={SETTINGS_BTN_PRIMARY}>
            {adding ? 'Verbinde…' : 'Postfach verbinden'}
          </button>
        </div>
      </form>
    );

  const backTarget = kind === 'choose' ? onCancel : goChoose;

  if (variant === 'embedded') {
    return (
      <div>
        {kind !== 'choose' ? (
          <button
            type="button"
            onClick={backTarget}
            disabled={adding}
            className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-app-text-muted hover:text-app-text disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
            Zurück
          </button>
        ) : null}
        {body}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 border-b border-app-border pb-4 mb-5">
        <button
          type="button"
          onClick={backTarget}
          disabled={adding}
          className="rounded-lg p-1.5 text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted transition-colors disabled:opacity-40"
          aria-label="Zurück"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={1.75} />
        </button>
        <h2 className="text-base font-bold text-app-text">Postfach hinzufügen</h2>
      </div>
      {body}
    </>
  );
}
